import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../hooks/useLanguage';
import OfferCard from './OfferCard';
import { getDisplayTtcEuros } from '../lib/offerPrice';
import { Button } from './ui/button';

/** Map legacy UI filter ids → catalog slugs stored on offers. */
const FILTER_ALIASES = {
  jetskis: 'jet-ski-tours',
  boats: 'boat-tours',
  sunset: 'sunset',
  family: 'family',
  private: 'private',
  fishing: 'fishing',
  'jet-ski-tours': 'jet-ski-tours',
  'boat-tours': 'boat-tours',
};

const FALLBACK_FILTERS = [
  { id: 'jet-ski-tours', labelKey: 'filters.jetskis' },
  { id: 'boat-tours', labelKey: 'filters.boats' },
  { id: 'sunset', labelKey: 'filters.sunset' },
  { id: 'family', labelKey: 'filters.family' },
  { id: 'private', labelKey: 'filters.private' },
];

const OffersSection = ({ searchFilters }) => {
  const { t, language } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backendCategories, setBackendCategories] = useState([]);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    loadOffers();
    loadCategories();
  }, []);

  const formatDuration = (minutes) => {
    if (!minutes) return `1 ${t('offerCard.hour')}`;

    if (minutes < 60) {
      return `${minutes} ${t('offerCard.minutes')}`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return hours === 1 ? `1 ${t('offerCard.hour')}` : `${hours} ${t('offerCard.hours')}`;
    }

    return `${hours}h${remainingMinutes < 10 ? '0' : ''}${remainingMinutes}`;
  };

  const loadOffers = async () => {
    try {
      const [response, transResponse] = await Promise.all([
        axios.get(`${API_URL}/api/wowbooking/offers?visible_only=true`),
        axios.get(`${API_URL}/api/offer-translations`).catch(() => ({ data: {} }))
      ]);
      const apiOffers = Array.isArray(response.data) ? response.data : [];
      const allTranslations = transResponse.data || {};

      // Transform API offers to match frontend format
      const transformedOffers = apiOffers.map(offer => {
        if (offer.is_bundle) {
          const categoryList =
            Array.isArray(offer.categories) && offer.categories.length > 0
              ? offer.categories
              : [offer.primary_category || offer.category || 'boat-tours'].flat().filter(Boolean);
          const primarySlug =
            offer.primary_category ||
            (typeof offer.category === 'string' ? offer.category : categoryList[0]);
          const firstVar = (offer.variants && offer.variants[0]) || {};
          const minM = offer.duration_minutes_min ?? firstVar.duration_minutes ?? 60;
          const maxM = offer.duration_minutes_max ?? minM;
          const priceFrom = Math.round(parseFloat(offer.price_ttc_min ?? 0));
          return {
            id: String(offer.id),
            isBundle: true,
            bundleSlug: offer.bundle_slug,
            title: offer.title,
            description: '',
            price: priceFrom,
            priceFrom,
            durationFromLabel: formatDuration(minM),
            durationToLabel: formatDuration(maxM),
            duration:
              minM === maxM
                ? formatDuration(minM)
                : `${formatDuration(minM)} – ${formatDuration(maxM)}`,
            capacity: `${t('offerCard.upTo')} ${firstVar.max_capacity || 2}`,
            image:
              offer.featured_image_url ||
              'https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=800&q=80',
            badge: t('offerCard.bundleBadge'),
            maxCapacity: firstVar.max_capacity || 99,
            category: categoryList,
            primaryCategory: primarySlug,
            slug: offer.slug,
            slugCategory: offer.slugCategory || offer.primary_category || primarySlug,
            features: [
              t('offerCard.bundleBadge'),
              formatDuration(minM),
              maxM !== minM ? formatDuration(maxM) : null,
            ].filter(Boolean),
          };
        }

        const priceTTC = getDisplayTtcEuros(offer);
        const customTrans = allTranslations[String(offer.id)] || {};

        const categoryList =
          Array.isArray(offer.categories) && offer.categories.length > 0
            ? offer.categories
            : determineCategoryFallback(offer);
        const primarySlug =
          offer.primary_category ||
          (typeof offer.category === 'string' ? offer.category : null) ||
          (categoryList.length > 0 ? categoryList[0] : null);

        return {
          id: offer.id.toString(),
          title: customTrans?.title?.[language] || offer.title,
          description: customTrans?.description?.[language] || offer.description?.[language] || offer.description?.fr || '',
          price: priceTTC,
          duration: formatDuration(offer.duration_minutes),
          capacity: `${t('offerCard.upTo')} ${offer.max_capacity || 2}`,
          image: offer.featured_image_url || 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=800&q=80',
          badge: offer.id <= 2 ? t('offerCard.badgeBestseller') : (offer.id === 4 ? t('offerCard.badgeNew') : (offer.id % 3 === 0 ? t('offerCard.badgePopular') : null)),
          maxCapacity: offer.max_capacity || 99,
          category: categoryList,
          primaryCategory: primarySlug,
          slug: offer.slug,
          slugCategory: offer.primary_category || primarySlug,
          features: [
            offer.offer_type === 'tour' ? t('offerCard.guidedTour') : t('offerCard.selfGuided'),
            formatDuration(offer.duration_minutes || 60),
            offer.max_capacity ? `${t('offerCard.upTo')} ${offer.max_capacity} ${t('offerCard.people')}` : ''
          ].filter(Boolean)
        };
      });

      setOffers(transformedOffers);
    } catch (error) {
      console.error('Failed to load offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/wowbooking/categories`);
      const cats = res.data?.categories || [];
      if (cats.length > 0) setBackendCategories(cats);
    } catch (e) {
      console.log('Failed to load categories, using fallback');
    }
  };

  const determineCategoryFallback = (offer) => {
    const title = (offer.title || '').toLowerCase();
    const primary =
      offer.primary_category ||
      (typeof offer.category === 'string' ? offer.category : null);
    const categories = [];
    if (primary) categories.push(primary);
    if (title.includes('jet ski') || title.includes('jetski')) categories.push('jet-ski-tours');
    if (
      title.includes('boat') ||
      title.includes('bateau') ||
      title.includes('doqueve') ||
      title.includes('pêche') ||
      title.includes('peche')
    ) {
      categories.push('boat-tours');
    }
    if (title.includes('sunset') || title.includes('coucher') || title.includes('champagne')) {
      categories.push('sunset');
      categories.push('boat-tours');
    }
    if (
      title.includes('family') ||
      title.includes('famille') ||
      title.includes('dauphins') ||
      title.includes('dolphin')
    ) {
      categories.push('family');
      categories.push('boat-tours');
    }
    if (title.includes('private') || title.includes('privé') || title.includes('prive')) {
      categories.push('private');
    }
    if (title.includes('pêche') || title.includes('peche') || title.includes('fishing')) {
      categories.push('fishing');
    }
    return categories.length > 0 ? [...new Set(categories)] : ['boat-tours'];
  };

  const offerCategories = (offer) =>
    (Array.isArray(offer.category) ? offer.category : [offer.category, offer.primaryCategory])
      .filter(Boolean)
      .map((c) => String(c).toLowerCase());

  // Checks if a category array matches jetski (handles any slug containing 'jet' or 'ski')
  const isJetSki = (cats) => cats.some((c) => c.includes('jet') || c.includes('ski'));
  const isBoat = (cats) =>
    cats.some(
      (c) =>
        c.includes('boat') ||
        c.includes('bateau') ||
        c.includes('family') ||
        c.includes('sunset') ||
        c.includes('private') ||
        c.includes('fishing') ||
        c.includes('activity')
    );

  const offerMatchesFilter = (offer, filterId) => {
    if (!filterId || filterId === 'all') return true;
    const normalized = FILTER_ALIASES[filterId] || filterId;
    const cats = offerCategories(offer);
    if (cats.includes(normalized) || cats.includes(filterId)) return true;
    // Broad aliases for older data
    if (normalized === 'jet-ski-tours') return isJetSki(cats);
    if (normalized === 'boat-tours') {
      return isBoat(cats) && !isJetSki(cats);
    }
    return false;
  };

  const isSearchActive = searchFilters !== null;

  const filteredOffers = (() => {
    let result = offers;

    if (isSearchActive) {
      const { resourceType, persons } = searchFilters;
      result = result.filter((offer) => {
        const cats = offerCategories(offer);
        const matchesType = resourceType === 'jetski' ? isJetSki(cats) : isBoat(cats);
        const maxCap = offer.maxCapacity ?? 99;
        const matchesPersons = maxCap >= persons;
        return matchesType && matchesPersons;
      });
    } else if (filter !== 'all') {
      result = result.filter((offer) => offerMatchesFilter(offer, filter));
    }

    return result;
  })();

  const categories =
    backendCategories.length > 0
      ? [
          { id: 'all', label: t('filters.all') },
          ...backendCategories.map((cat) => ({
            id: cat.slug,
            label: (cat.names && cat.names[language]) || cat.name,
          })),
        ]
      : [
          { id: 'all', label: t('filters.all') },
          ...FALLBACK_FILTERS.map((cat) => ({
            id: cat.id,
            label: t(cat.labelKey),
          })),
        ];

  return (
    <section id="offers" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-primary-950 mb-4 font-display">
            {isSearchActive ? t('search.resultsTitle') : t('sections.offersTitle')}
          </h2>
          {!isSearchActive && (
            <p className="text-lg text-gray-600">{t('sections.offersSubtitle')}</p>
          )}
        </div>

        {/* Category filters — hidden when search is active */}
        {!isSearchActive && (
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setFilter(cat.id)}
                className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                  filter === cat.id
                    ? 'bg-primary-950 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-600 hover:bg-gray-100 hover:text-primary-950 border border-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-950"></div>
            <p className="mt-4 text-gray-600">{t('offerCard.loading')}</p>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">{t('search.noResults')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredOffers.map((offer) => (
              <OfferCard key={offer.id} offer={offer} categoryCatalog={backendCategories} />
            ))}
          </div>
        )}

        {/* Show More */}
        {filteredOffers.length > 9 && (
          <div className="text-center mt-12">
            <Button variant="outline" className="px-8 border-primary-950 text-primary-950 hover:bg-primary-950 hover:text-white">
              {t('offerCard.showAll')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default OffersSection;
